import { PayloadAction } from '@reduxjs/toolkit';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { IPlacementRequirementKey } from 'app/models/Placement/PlacementDetails';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackPromise } from 'react-promise-tracker';
import { call, fork, getContext, put, all, select } from 'redux-saga/effects';
import { SetPlacementFailRiskRequirementMessage } from 'store/redux-store/new-placement/saga';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import InitPlacementSummary, { IConsolidatedRequirementResponse } from '../init.types';
import { ServiceConstants } from '../sagaHelper';
import { IPlacementSummaryRequest, placementSummaryActions } from '../slice';
import { selectPlacementConsolidatedDetails } from '../consolidatedStoreSelectors';

const TrackBookingConfirmationsWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-booking-confirmations-data');

export function* processBookingConfirmationRequirement(
  response: IConsolidatedRequirementResponse<any>,
  context: InitPlacementSummary,
) {
  try {
    if (!httpSuccess(response?.status)) {
      context.setServiceFailureByKey({ key: 'bookingConfirmationRequirement', failureStatus: true });
      return;
    }

    yield fork(common, response.data);

    context.setBookingConfirmationInfo(response.data);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'processBookingConfirmationRequirement',
        area: 'src/store/redux-store/placement-summary/common-saga/bookingConfirmationRequirement.ts',
      },
    });
  }
}

export function* requestBookingConfirmationRequirement(action: PayloadAction<IPlacementSummaryRequest>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');

    let response;

    const consolidatedDetails = yield select(selectPlacementConsolidatedDetails);
    if(action.payload.isInitLoad && consolidatedDetails){
      response = {
        status : 200,
        data :{
          bookingConfirmations : {
            arrived : consolidatedDetails.summary.booking.bookingConfirmationRequrement?.arrived?.data,
            directDeposit : consolidatedDetails.summary.booking.bookingConfirmationRequrement?.directDeposit?.data,
            wisely : consolidatedDetails.summary.booking.bookingConfirmationRequrement?.wisely?.data,
            nextStatusRequirement : consolidatedDetails.summary.booking.bookingConfirmationRequrement?.nextStatusRequirement,
          } 
        } 
      }
    }

    if(!response){
      response = yield call(
        TrackBookingConfirmationsWrapper,
        placementService.getStrikeRequirementsByContainer,
        action.payload.placementId,
        ...ServiceConstants.bookingConfirmation,
      );
    }
    
    if (httpSuccess(response?.status)) {
      yield fork(common, response.data.bookingConfirmations);

      yield put(placementSummaryActions.setBookingConfirmationInfo(response.data.bookingConfirmations));
      yield put(placementDetailsAction.resetForm());
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestBookingConfirmationRequirement',
        area: 'src/store/redux-store/placement-summary/common-saga/bookingConfirmationRequirement.ts',
      },
    });
  }
}

function* common(data) {
  yield all(
    [IPlacementRequirementKey.arrived, IPlacementRequirementKey.wisely].map(id =>
      put(
        placementStatusAction.removeFailedRequirement({
          requirement: { id },
        }),
      ),
    ),
  );

  yield fork(SetPlacementFailRiskRequirementMessage, {
    payload: {
      messageId: 2032,
      version: 'v1',
      passFailType: data?.arrived?.type,
      data: data?.arrived,
      messageTypeId: IPlacementRequirementKey.arrived,
      tooltipTypeParam: undefined,
      nextStatusRequirementParam: data?.nextStatusRequirement,
    },
    type: placementSummaryActions.setFailedRequirement.type,
  });

  yield fork(SetPlacementFailRiskRequirementMessage, {
    payload: {
      messageId: 2034,
      version: 'v1',
      passFailType: data?.wisely?.type,
      data: data?.wisely,
      messageTypeId: IPlacementRequirementKey.wisely,
      tooltipTypeParam: undefined,
      nextStatusRequirementParam: data?.nextStatusRequirement,
    },
    type: placementSummaryActions.setFailedRequirement.type,
  });
}
